/**
 * /help — landing for help features. Currently hosts the "AI-подборка"
 * widget: 4-field brief → OpenAI picks bloggers from the catalog → result
 * lands as a Collection so the existing launch/extend actions work as-is.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, Loader2, Sparkles, Wallet, Wand2 } from "lucide-react";
import { useCreateAiCollection, useGeoRegions } from "@/shared/api/wb";
import { Button, Card, Eyebrow, Field } from "@/shared/ui";
import { cn } from "@/shared/lib/cn";

export function HelpPage() {
  return (
    <div className="p-7 pb-16 max-w-[920px]">
      <div className="mb-7">
        <Eyebrow className="mb-1.5">Помощь</Eyebrow>
        <h1 className="font-display font-extrabold tracking-tight text-[28px] leading-tight">
          Чем можем помочь
        </h1>
        <p className="text-[14px] text-ink-60 mt-2 max-w-[640px]">
          Готовые инструменты, чтобы быстрее запустить РК — пока тут AI-подбор каналов
          по вашему сайту и бюджету. Скоро добавим помощь по креативам и общению с блогерами.
        </p>
      </div>

      <AiCollectionWidget />
    </div>
  );
}

function AiCollectionWidget() {
  const navigate = useNavigate();
  const { data: geoRegions } = useGeoRegions();
  const ai = useCreateAiCollection();

  const [url, setUrl] = useState("");
  const [budget, setBudget] = useState<string>("");
  const [what, setWhat] = useState("");
  const [geo, setGeo] = useState<string[]>([]);
  const [touched, setTouched] = useState(false);

  const budgetNum = Number(budget.replace(/\s/g, ""));
  const urlValid = url.trim().length > 3 && /\./.test(url);
  const budgetValid = Number.isFinite(budgetNum) && budgetNum >= 5000;
  const whatValid = what.trim().length >= 10;
  const canSubmit = urlValid && budgetValid && whatValid && !ai.isPending;

  function toggleGeo(id: string) {
    setGeo((g) => (g.includes(id) ? g.filter((x) => x !== id) : [...g, id]));
  }

  async function onSubmit() {
    setTouched(true);
    if (!canSubmit) return;
    try {
      const res = await ai.mutateAsync({ url: url.trim(), budget: budgetNum, what: what.trim(), geo });
      navigate(`/collections/${res.collection_id}`);
    } catch {
      // surface error inline via ai.isError below
    }
  }

  return (
    <Card className="!p-0 overflow-hidden">
      <div className="px-7 pt-6 pb-5 border-b border-ink-12 bg-[var(--bg-2)] flex items-start gap-4">
        <div className="w-11 h-11 rounded-md bg-lime grid place-items-center text-[var(--fg-on-accent)] flex-shrink-0">
          <Sparkles size={20} strokeWidth={2.2} />
        </div>
        <div>
          <div className="font-display font-extrabold text-[20px] tracking-tight">AI-подборка блогеров</div>
          <div className="text-[13px] text-ink-60 mt-1 leading-relaxed">
            Опишите продукт и бюджет — мы заглянем на ваш сайт, поймём позиционирование
            и соберём подборку каналов из каталога с пояснениями, почему каждый подходит.
          </div>
        </div>
      </div>

      <div className="p-7 grid gap-5">
        <Field
          label="Ссылка на сайт или TG-канал"
          placeholder="https://your-brand.ru или https://t.me/your_channel"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          error={touched && !urlValid ? "Похоже на некорректную ссылку" : undefined}
          hint={!touched || urlValid ? "Мы прочитаем главную, чтобы лучше понять бренд" : undefined}
        />

        <Field
          label="Бюджет, ₽"
          placeholder="например, 250 000"
          inputMode="numeric"
          value={budget}
          onChange={(e) => setBudget(e.target.value.replace(/[^\d\s]/g, ""))}
          error={touched && !budgetValid ? "Укажите бюджет от 5 000 ₽" : undefined}
          hint={!touched || budgetValid ? "Можно с пробелами, например 250 000" : undefined}
        />

        <label className="flex flex-col gap-1.5">
          <span className="text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40">
            Что будем рекламировать
          </span>
          <textarea
            value={what}
            onChange={(e) => setWhat(e.target.value)}
            rows={3}
            placeholder="Короткое описание продукта, оффера и ЦА. Например: «онлайн-курсы по аналитике данных для взрослых, которые хотят сменить профессию»"
            className={cn(
              "px-4 py-3 rounded-md border bg-white text-ink text-[15px] placeholder:text-ink-40 focus:border-ink focus:outline-none transition-colors resize-none",
              touched && !whatValid ? "border-danger" : "border-ink-12",
            )}
          />
          {touched && !whatValid
            ? <span className="text-[12px] text-danger">Опишите подробнее — хотя бы пару предложений</span>
            : <span className="text-[12px] text-ink-60">Чем точнее описание — тем точнее подбор</span>}
        </label>

        <div className="flex flex-col gap-1.5">
          <span className="text-[11px] font-display font-bold uppercase tracking-[0.07em] text-ink-40">
            География аудитории
            <span className="ml-2 normal-case tracking-normal text-ink-40 font-medium">
              (необязательно — отметьте, если важна)
            </span>
          </span>
          <div className="flex flex-wrap gap-1.5">
            {(geoRegions ?? []).map((r) => {
              const on = geo.includes(r.id);
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => toggleGeo(r.id)}
                  className={cn(
                    "inline-flex items-center gap-1.5 h-8 px-3 rounded-pill border text-[13px] font-display font-semibold transition-colors",
                    on
                      ? "bg-ink text-white border-ink"
                      : "bg-transparent text-ink-60 border-ink-12 hover:border-ink/40 hover:text-ink",
                  )}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
        </div>

        {ai.isError && (
          <div className="px-4 py-3 rounded-md bg-danger/10 text-danger text-[13px]">
            Не удалось собрать подборку. Попробуйте ещё раз или измените параметры.
          </div>
        )}

        <div className="flex items-center gap-3 pt-1">
          <Button onClick={onSubmit} disabled={!canSubmit}>
            {ai.isPending ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
            {ai.isPending ? "Собираем подборку…" : "Собрать подборку"}
          </Button>
          <div className="text-[12px] text-ink-40">
            Обычно занимает 5–15 секунд
          </div>
        </div>
      </div>

      {ai.isPending && <ProgressOverlay url={url} />}
    </Card>
  );
}

function ProgressOverlay({ url }: { url: string }) {
  return (
    <div className="px-7 pb-7 -mt-2">
      <div className="rounded-md border border-ink-12 bg-[var(--bg-2)] px-5 py-4 grid gap-2.5">
        <ProgressStep icon={Globe} label={`Читаем ${prettyHost(url)}`} active />
        <ProgressStep icon={Wallet} label="Подбираем каналы под бюджет и тематику" active />
        <ProgressStep icon={Sparkles} label="Готовим подборку и пояснения" active />
      </div>
    </div>
  );
}

function ProgressStep({ icon: Icon, label, active }: {
  icon: typeof Globe; label: string; active: boolean;
}) {
  return (
    <div className="flex items-center gap-2.5 text-[13px]">
      <div className={cn(
        "w-6 h-6 grid place-items-center rounded-full",
        active ? "bg-lime text-[var(--fg-on-accent)]" : "bg-ink-6 text-ink-40",
      )}>
        {active ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />}
      </div>
      <span className="text-ink-60">{label}</span>
    </div>
  );
}

function prettyHost(url: string): string {
  try {
    const u = new URL(/^https?:\/\//i.test(url) ? url : "https://" + url);
    return u.hostname.replace(/^www\./, "");
  } catch {
    return url || "сайт";
  }
}
